/**
 * Links that Prime and the agents write. An app path (`/projects/<id>`) goes
 * through the router; an outside link (http or https, nothing else) never opens
 * directly, it goes through `/leave?to=<url>` which says where it leads first.
 * `internal/assistant/watches.go` `LeavePath` does the same for notifications.
 */

export type LinkTarget = { kind: 'app'; path: string } | { kind: 'out'; url: string; host: string }

// whitespace, backslashes and control characters are never in a link we follow
const CONTROL = {
  test: (s: string) =>
    /[\s\\]/.test(s) || [...s].some((c) => c.charCodeAt(0) < 32 || c.charCodeAt(0) === 127),
}

/** The target of a link, or null when it is neither an app path nor http(s). */
export const linkTarget = (href: string): LinkTarget | null => {
  const h = href.trim()
  if (!h) return null
  if (h.startsWith('/')) {
    if (h.startsWith('//') || CONTROL.test(h)) return null
    return { kind: 'app', path: h }
  }
  if (CONTROL.test(h)) return null
  let u: URL
  try {
    u = new URL(h)
  } catch {
    return null
  }
  if ((u.protocol !== 'https:' && u.protocol !== 'http:') || !u.hostname) return null
  return { kind: 'out', url: u.href, host: u.host }
}

/** Where a link is followed inside the app: the path itself, or the warning page. */
export const inAppPath = (t: LinkTarget): string =>
  t.kind === 'app' ? t.path : `/leave?to=${encodeURIComponent(t.url)}`

export type InlinePart = { text: string } | { label: string; href: string }

// [label](target) first, then a bare http(s) URL
const LINK = /\[([^\]\n]+)\]\(([^)\s]+)\)|(https?:\/\/[^\s<>]+)/g

/** Split a run of plain text into text and links; a link whose target is refused stays as its words. */
export const splitLinks = (text: string): InlinePart[] => {
  const out: InlinePart[] = []
  let last = 0
  const push = (s: string) => {
    if (!s) return
    const prev = out[out.length - 1]
    if (prev && 'text' in prev) prev.text += s
    else out.push({ text: s })
  }
  for (const m of text.matchAll(LINK)) {
    const at = m.index
    push(text.slice(last, at))
    last = at + m[0].length
    if (m[1] !== undefined) {
      if (linkTarget(m[2]!)) out.push({ label: m[1], href: m[2]! })
      else push(m[1])
      continue
    }
    // a bare URL: leave trailing punctuation (and an unmatched bracket) outside it
    let url = m[3]!
    let tail = ''
    for (;;) {
      const c = url[url.length - 1]
      if (c && /[.,;:!?'"]/.test(c)) {
        tail = c + tail
        url = url.slice(0, -1)
      } else if (c === ')' && !url.includes('(')) {
        tail = c + tail
        url = url.slice(0, -1)
      } else break
    }
    if (linkTarget(url)) out.push({ label: url, href: url })
    else push(url)
    push(tail)
  }
  push(text.slice(last))
  return out
}
